import { createVNode, render } from 'vue'
import { lockBody, unlockBody } from 'src/composables/useModalBodyLock'

// Guard utility: some server/test environments have no DOM. Use these guards
const _hasDOM = typeof document !== 'undefined' && typeof window !== 'undefined'

// Programmatic lightbox: openLightbox(images, startIndex)
// images: array of strings or objects { src, title, description }
// Cache the discovered app context so subsequent calls are fast and avoid
// repeatedly querying the DOM. If the app is ever re-mounted this cache can be
// invalidated by setting `cachedAppContext = null` (not currently needed).
let cachedAppContext = null

function findAppContext() {
  if (cachedAppContext) return cachedAppContext
  if (!_hasDOM) return null
  try {
    // Common mount roots used in this project: #app (vite/quasar), #q-app (quasar default)
    const roots = ['#app', '#q-app', '[data-v-app]']
    for (const sel of roots) {
      const el = document.querySelector(sel)
      if (el && el.__vue_app__) {
        // Vue 3 app attaches to the mount element as __vue_app__. The internal context is
        // available as _context on the app instance. Fall back to .context if present.
        const detected = el.__vue_app__._context || el.__vue_app__.context || null
        if (detected) {
          cachedAppContext = detected
          return cachedAppContext
        }
      }
    }
    // fallback: check body
    if (document.body && document.body.__vue_app__) {
      const detected =
        document.body.__vue_app__._context || document.body.__vue_app__.context || null
      if (detected) {
        cachedAppContext = detected
        return cachedAppContext
      }
    }
  } catch {
    // ignore
  }
  return null
}

export async function openLightbox(images = [], startIndex = 0) {
  if (!images || !images.length) return Promise.resolve()

  // Dynamically import the local EasyLightbox wrapper (it uses vue-easy-lightbox internally and supports title/description)
  const module = await import('src/components/ui/EasyLightbox.vue')
  const EasyLightbox = module.default || module

  if (!_hasDOM) {
    // No DOM available (SSR/tests) — noop lightbox. Return a controller-like object
    return Promise.resolve({ close: () => {} })
  }

  const container = document.createElement('div')
  document.body.appendChild(container)

  // Prevent background scrolling while lightbox is open (use centralized helper)
  const prevOverflow = lockBody()

  // Normalize images into objects { src, title, description }
  const normalized = images.map((i) => {
    if (!i) return { src: '' }
    if (typeof i === 'string') return { src: i, title: '' }
    return {
      src: i.src || i.image || i.url || '',
      title: i.title || i.name || i.alt || '',
      description: i.description || i.desc || ''
    }
  })

  // Props for the wrapper component
  const props = {
    modelValue: true,
    images: normalized,
    index: startIndex
  }

  // Create vnode using the EasyLightbox wrapper
  const vnode = createVNode(EasyLightbox, props)

  // Try to auto-discover the appContext from the mounted app so injected plugins
  // (Quasar, global properties) are available inside the programmatically mounted component.
  const detected = findAppContext()
  if (detected) vnode.appContext = detected

  // Listen for hide to unmount
  vnode.props = vnode.props || {}
  // EasyLightbox emits 'hide' when closed; handle unmount and restore scroll
  vnode.props.onHide = () => {
    // restore scroll
    try {
      unlockBody(prevOverflow)
    } catch {
      void 0
    }
    render(null, container)
    if (container.parentNode) container.parentNode.removeChild(container)
  }

  render(vnode, container)

  // Return a controller to programmatically close if needed
  return {
    close: () => {
      // set visible to false by re-rendering vnode with visible=false
      try {
        unlockBody(prevOverflow)
      } catch {
        void 0
      }
      const closeVNode = createVNode(EasyLightbox, {
        modelValue: false,
        images: normalized,
        index: startIndex
      })
      render(closeVNode, container)
    }
  }
}
